import { GROUPED_CHART_COLORS } from "@/constants/colors";

import type { ComponentApi } from "@a2ui/web_core/v0_9";
import { CommonSchemas } from "@a2ui/web_core/v0_9";
import { z } from "zod";

export const REPORT_CATALOG_ID = "orbis.analysis-report.v1";
export const REPORT_CATALOG_VERSION = 1;

// Each surface tree starts at the fixed "root" id; per-surface root ids are
// Phase 2 work.
export const REPORT_ROOT_ID = "root";

const MAX_CHART_SERIES = 8;
const MAX_CHART_ROWS = 50;
const MAX_CARD_COUNT = 4;
const MAX_GRID_ROWS = 50;
const MAX_GRID_COLUMNS = 8;

// CommonSchemas mark shared types with a `REF:<pointer>|<prose>` description so
// buildCatalogDocument can turn them into a $ref. Keep the pointer and replace
// only the prose: a plain .describe() would drop the marker and inline the
// whole shared type instead of pointing at common_types.json.
function describeRef<T extends z.ZodTypeAny>(schema: T, prose: string): T {
  const [pointer] = (schema.description ?? "").split("|");
  return schema.describe(`${pointer}|${prose}`) as T;
}

// Cells are either numbers or strings; rows are records of cells.
const CELL_SCHEMA = z.union([z.number(), z.string()]);
const ROW_SCHEMA = z
  .record(CELL_SCHEMA)
  .describe(
    "One row of tabular data. Keys are the column or series keys referenced elsewhere in the component; values are raw numbers or strings."
  );

// The model may only name a tone, never a colour: the same rule as colorIndex
// on charts. Tones must stay in step with BadgeVariantType in ui/badge.tsx.
const BADGE_TONE_SCHEMA = z.enum(["success", "warning", "disabled"]);

const CHART_SERIES_SCHEMA = z
  .array(
    z.object({
      dataKey: z
        .string()
        .describe("Key in each data row holding this series' numeric value."),
      name: z.string().describe("Series label shown in the legend."),
      colorIndex: z
        .number()
        .int()
        .min(0)
        .max(GROUPED_CHART_COLORS.length - 1)
        .describe(
          "Index into the chart palette. Pick a different index per series; never write a colour value."
        )
    })
  )
  .max(MAX_CHART_SERIES)
  .describe("One entry per plotted measure, at most 8.");

const CARD_SCHEMA = z.object({
  label: describeRef(
    CommonSchemas.DynamicString,
    'What the number measures, e.g. "Grids scored".'
  ),
  value: describeRef(
    CommonSchemas.DynamicNumber,
    "The raw number. Do not pre-format it."
  ),
  caption: describeRef(
    CommonSchemas.DynamicString,
    'Short qualifier under the value, e.g. "vs. last quarter".'
  )
});

const GRID_COLUMN_SCHEMA = z.object({
  key: z
    .string()
    .describe("Key looked up in each row object to fill this column."),
  label: z.string().describe("Column header text."),
  align: z
    .enum(["left", "right"])
    .describe("Cell alignment. Use 'right' for numeric columns."),
  render: z
    .enum(["badge", "accent"])
    .optional()
    .describe(
      "Render style for this column's cells. 'badge' draws a coloured pill, 'accent' emphasises the headline number of the table. Omit for plain text."
    ),
  tones: z
    .record(BADGE_TONE_SCHEMA)
    .optional()
    .describe(
      'Maps a cell value to a badge colour, e.g. { "High": "success", "Medium": "warning" }. Read only when render is "badge"; a value missing from the map falls back to plain text.'
    )
});

export const COMPONENT_APIS: ComponentApi[] = [
  {
    name: "ReportSection",
    schema: z
      .object({
        children: describeRef(
          CommonSchemas.ChildList,
          "Ids of the components stacked in this section, in the order they should read."
        )
      })
      .describe(
        "A vertical stack of components. The only layout primitive: use one section per topic of the report, and one as the 'root' component."
      )
  },
  {
    name: "ReportText",
    schema: z
      .object({
        markdown: describeRef(
          CommonSchemas.DynamicString,
          "GitHub-flavoured markdown. Headings, lists, emphasis and links are supported."
        )
      })
      .describe(
        "Markdown prose. The fallback component: anything without a dedicated component goes here."
      )
  },
  {
    name: "ReportChart",
    schema: z
      .object({
        title: describeRef(
          CommonSchemas.DynamicString,
          "Heading shown above the chart."
        ),
        baseChart: z
          .enum(["bar", "line", "pie"])
          .describe(
            "Chart family. 'pie' renders a donut and plots only the first series."
          ),
        orientation: z
          .enum(["vertical", "horizontal"])
          .default("vertical")
          .describe(
            "Bar direction. 'horizontal' suits long category labels. Ignored by 'line' and 'pie'."
          ),
        xAxisKey: z
          .string()
          .describe("Key in each data row holding the category label."),
        series: CHART_SERIES_SCHEMA,
        data: z
          .union([
            z.array(ROW_SCHEMA).max(MAX_CHART_ROWS),
            CommonSchemas.DataBinding
          ])
          .describe(
            "Chart rows, at most 50. Either written inline or bound to an array in the data model."
          )
      })
      .describe(
        "A bar, line or donut chart over tabular rows. Every row supplies one category and one value per series."
      )
  },
  {
    name: "ReportKpi",
    schema: z
      .object({
        cards: z
          .union([
            z.array(CARD_SCHEMA).max(MAX_CARD_COUNT),
            CommonSchemas.DataBinding
          ])
          .describe(
            "The cards to show, at most 4. Either written inline or bound to an array in the data model."
          )
      })
      .describe(
        "A row of KPI cards for the headline numbers. Use it once, near the top of the report."
      )
  },
  {
    name: "ReportTable",
    schema: z
      .object({
        columns: z
          .union([
            z.array(GRID_COLUMN_SCHEMA).max(MAX_GRID_COLUMNS),
            CommonSchemas.DataBinding
          ])
          .describe("Column definitions, at most 8, in display order."),
        rows: z
          .union([
            z.array(ROW_SCHEMA).max(MAX_GRID_ROWS),
            CommonSchemas.DataBinding
          ])
          .describe(
            "Table rows, at most 50. Each row is looked up by the column keys; missing keys render empty."
          )
      })
      .describe(
        "A read-only table. Renders nothing unless both columns and rows are non-empty."
      )
  }
];

// The model may only pick a palette index, never a hex value. Out-of-range
// indexes clamp to a safe entry instead of passing a raw string to `fill`.
export function colorForColorIndex(index: number): string {
  const safe = Math.max(0, Math.floor(index));
  return GROUPED_CHART_COLORS[safe < GROUPED_CHART_COLORS.length ? safe : 0];
}

// A stored report is read-only: the catalog declares no action, so nothing it
// renders can call back to the agent. That is why there is no map-link
// component here — the agent's live summary had one, a stored report cannot.
export const reportCatalogContract = {
  catalogId: REPORT_CATALOG_ID,
  version: REPORT_CATALOG_VERSION,
  title: "Orbis Analysis Report Catalog",
  description:
    "Components the analysis report agent may use to write a stored analysis report. Owned by the frontend, which holds the implementations.",
  apis: COMPONENT_APIS
};
